import { Card, Flex, Progress, Text } from "@radix-ui/themes";
import { HardDrive } from "lucide-react";
import { ManagerGalleryStorage } from "@/types/manager_gallery";
import { humanSize } from "./format";

interface Props {
  storage: ManagerGalleryStorage;
}

// Amber before the wall, red at it. Somebody about to upload eight gigabytes
// wants to know they won't fit *before* they start, not after.
function toneFor(percent: number): { color: "red" | "amber" | "green"; note: string | null } {
  if (percent >= 100) return { color: "red", note: "Full — uploads will be refused." };
  if (percent >= 90) return { color: "red", note: "Almost full." };
  if (percent >= 75) return { color: "amber", note: "Filling up." };
  return { color: "green", note: null };
}

export default function StorageMeter({ storage }: Props) {
  const { used_bytes, quota_bytes, club_name } = storage;
  const percent = quota_bytes > 0 ? Math.min((used_bytes / quota_bytes) * 100, 100) : 100;
  const free = Math.max(quota_bytes - used_bytes, 0);
  const { color, note } = toneFor(percent);

  return (
    <Card>
      <Flex direction="column" gap="2">
        <Flex align="center" justify="between" gap="3" wrap="wrap">
          <Flex align="center" gap="2">
            <HardDrive size={14} color="var(--gray-9)" />
            <Text size="2" weight="medium">
              {club_name} storage
            </Text>
          </Flex>
          <Text size="2" color="gray">
            {humanSize(used_bytes)} of {humanSize(quota_bytes)} used
          </Text>
        </Flex>

        <Progress value={percent} color={color} size="2" />

        <Flex align="center" justify="between" gap="3" wrap="wrap">
          <Text size="1" color="gray">
            {humanSize(free)} free
          </Text>
          {note && (
            <Text size="1" color={color === "green" ? "gray" : color}>
              {note}
            </Text>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}
